// The paket documents by type (B5), inside Persiapan "Unggah dan Baca
// Dokumen Paket" (project/PaketStages.tsx): the four main documents
// (Dokumen Pemilihan, Spesifikasi Teknis/KAK, BoQ, Draft Kontrak) and
// Dokumen Lainnya, each with its files (GET /projects/:id/files groups) and
// its own Unggah (POST /projects/:id/files with the docType). Every file can
// be moved to another type (PATCH /projects/:id/files/:fileId); a readable
// one can be read into the requirement matrix (Susun Matriks).
import { useRef, useState, type ChangeEvent } from "react";
import * as API from "../api";
import type { ProjectDocType, ProjectFile, ProjectFileGroup, ProjectFiles } from "../api";
import { DRIVE_OFF_TEXT, checkProjectFiles, formatFileSize } from "../project/files";
import { Notice } from "../ui/components";
import { Glyph } from "../ui/icons";

export const DOC_TYPE_LABEL: Record<ProjectDocType, string> = {
  dokumen_pemilihan: "Dokumen Pemilihan",
  spesifikasi_teknis: "Spesifikasi Teknis/KAK",
  boq: "BoQ",
  draft_kontrak: "Draft Kontrak",
  lainnya: "Dokumen Lainnya",
};

const DOC_TYPE_HINT: Record<ProjectDocType, string> = {
  dokumen_pemilihan: "LDP/LDK, IKP, jadwal, dan adendum dari Pokja.",
  spesifikasi_teknis: "Spesifikasi teknis atau Kerangka Acuan Kerja.",
  boq: "Daftar kuantitas dan harga (Bill of Quantity).",
  draft_kontrak: "Rancangan kontrak dan syarat-syarat kontrak.",
  lainnya: "Gambar, lampiran, atau dokumen pendukung lain.",
};

/** Files whose text the API can read. */
export const READABLE = /\.(pdf|docx|xlsx|txt|md|csv)$/i;
const ACCEPT = ".pdf,.docx,.xlsx,.txt,.png,.jpg,.jpeg,.webp";

/** The groups in PROJECT_DOC_TYPES order (built from the files when an older answer has none). */
export function groupsOf(view: ProjectFiles | null | undefined): ProjectFileGroup[] {
  const files = view?.files ?? [];
  return API.PROJECT_DOC_TYPES.map((docType) => {
    const sent = view?.groups?.find((g) => g.docType === docType);
    return sent ?? { docType, label: DOC_TYPE_LABEL[docType], files: files.filter((f) => (f.docType ?? "lainnya") === docType) };
  });
}

/** True when the files cannot be stored (Drive not connected). */
export function driveOff(view: ProjectFiles | null | undefined, error: unknown): boolean {
  return view?.driveConnected === false || (error instanceof API.ApiError && error.status === 503);
}

function uploadError(err: unknown): string {
  if (err instanceof API.ApiError && err.status === 503) return `${DRIVE_OFF_TEXT} Dokumen belum bisa diunggah; hubungi admin.`;
  return API.errorMessage(err);
}

export interface PaketDocsProps {
  projectId: string;
  view: ProjectFiles | null;
  error: unknown;
  /** The list changed: read it again. */
  onChanged: () => void;
  /** Susun Matriks from one file; resolves when the matrix is ready. */
  onAnalyze: (file: ProjectFile) => Promise<void>;
}

export function PaketDocs({ projectId, view, error: loadError, onChanged, onAnalyze }: PaketDocsProps) {
  const pickers = useRef<Partial<Record<ProjectDocType, HTMLInputElement | null>>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const off = driveOff(view, loadError);
  const groups = groupsOf(view);

  const onPick = async (docType: ProjectDocType, e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.currentTarget.files ?? []);
    e.currentTarget.value = "";
    if (picked.length === 0) return;
    const problem = checkProjectFiles(picked);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(`upload:${docType}`);
    setError(null);
    try {
      await API.uploadProjectFiles(projectId, docType, picked);
      onChanged();
    } catch (err) {
      setError(uploadError(err));
    } finally {
      setBusy(null);
    }
  };

  const move = async (file: ProjectFile, docType: ProjectDocType) => {
    setBusy(`type:${file.driveFileId}`);
    setError(null);
    try {
      await API.setProjectFileType(projectId, file.driveFileId, docType);
      onChanged();
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const analyze = async (file: ProjectFile) => {
    setBusy(`read:${file.driveFileId}`);
    setError(null);
    try {
      await onAnalyze(file);
    } catch (err) {
      setError(API.errorMessage(err));
      setBusy(null);
    }
  };

  return (
    <div className="pyd">
      {off ? <p className="pks-caption">Penyimpanan file belum terhubung, jadi dokumen belum bisa diunggah. Tempel teks persyaratan langsung di Review Persyaratan.</p> : null}
      {error ? (
        <Notice tone="danger" title="Dokumen belum tersimpan">
          <p>{error}</p>
        </Notice>
      ) : null}
      {!view && !loadError ? <p className="pks-caption">Memuat dokumen project</p> : null}
      <ul className="pyd-groups" aria-label="Dokumen paket per jenis">
        {groups.map((g) => {
          const label = DOC_TYPE_LABEL[g.docType];
          const uploading = busy === `upload:${g.docType}`;
          return (
            <li key={g.docType} className="pyd-group" data-doc-type={g.docType}>
              <div className="pyd-group-head">
                <span className="pyd-group-text">
                  <span className="pyd-group-title">
                    <span>{label}</span>
                    <span className="pyd-count num">{g.files.length}</span>
                  </span>
                  <span className="pyd-group-hint">{DOC_TYPE_HINT[g.docType]}</span>
                </span>
                <input
                  ref={(el) => {
                    pickers.current[g.docType] = el;
                  }}
                  type="file"
                  multiple
                  accept={ACCEPT}
                  hidden
                  aria-label={`Pilih file ${label}`}
                  onChange={(e) => void onPick(g.docType, e)}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  aria-label={`Unggah ${label}`}
                  disabled={busy !== null || off}
                  aria-busy={uploading || undefined}
                  onClick={() => pickers.current[g.docType]?.click()}
                >
                  <Glyph name="add" size={18} />
                  <span>{uploading ? "Mengunggah" : "Unggah"}</span>
                </button>
              </div>
              {g.files.length === 0 ? (
                <p className="pyd-empty">Belum ada file.</p>
              ) : (
                <ul className="rows pyd-files" aria-label={`File ${label}`}>
                  {g.files.map((f) => (
                    <li key={f.driveFileId} className="row tp-row pyd-file">
                      <span className="row-main">
                        <span className="row-title">{f.name}</span>
                        {f.size ? <span className="row-meta num">{formatFileSize(f.size)}</span> : null}
                      </span>
                      <span className="row-side pyd-file-side">
                        <label className="wk-mini">
                          <span>Jenis dokumen</span>
                          <select
                            value={f.docType ?? g.docType}
                            disabled={busy !== null}
                            aria-label={`Jenis dokumen ${f.name}`}
                            onChange={(e) => void move(f, e.currentTarget.value as ProjectDocType)}
                          >
                            {API.PROJECT_DOC_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {DOC_TYPE_LABEL[t]}
                              </option>
                            ))}
                          </select>
                        </label>
                        {READABLE.test(f.name) ? (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={busy !== null}
                            aria-busy={busy === `read:${f.driveFileId}` || undefined}
                            onClick={() => void analyze(f)}
                          >
                            <Glyph name="taskSquare" />
                            <span>{busy === `read:${f.driveFileId}` ? "Pluto membaca" : "Susun Matriks"}</span>
                          </button>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
